import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { ApiError, del, get, patch, post, type Destination as ApiDestination, type DestinationInput, type PlatformAccounts, type PlatformConnectError, type Profile, type StreamKey } from '../../api'
import { useSession } from '../../session'
import { useNow } from '../../hooks'
import { buildModel } from '../adapter'
import { t } from '../../i18n'

export const ACCOUNT_LABEL = { youtube: 'YouTube', twitch: 'Twitch', kick: 'Kick' } as const
export type AccountPlatform = keyof typeof ACCOUNT_LABEL

/**
 * The most outputs a user may have when the center does not say: the build's VITE_MAX_DESTINATIONS
 * (controlapi MAX_DESTINATIONS). Never a number in the UI code; use useMaxOutputs().
 */
export const MAX_OUTPUTS = Number(import.meta.env.VITE_MAX_DESTINATIONS ?? 10)

/**
 * The most outputs the user may have: GET /api/me limits.destinations (the plan's, or the
 * center's MAX_DESTINATIONS with billing off; BILL-1 B2), else the build's default.
 */
export function useMaxOutputs(): number {
  const { user } = useSession()
  const v = user?.limits?.destinations
  // null (a plan without a limit): the center still applies MAX_DESTINATIONS.
  return typeof v === 'number' ? v : MAX_OUTPUTS
}

/** Texts for ?platform_error=<p>&reason=<r> after the OAuth round trip (docs/m15-live-stats-ui-handoff.md 3). */
function connectError(label: string, reason: string | null): string {
  switch (reason as PlatformConnectError | null) {
    case 'denied':
      return t('connect.denied', { platform: label })
    case 'expired':
      return t('connect.expired')
    case 'no_channel':
      return t('connect.noChannel')
    default:
      return t('connect.failed', { platform: label })
  }
}

/**
 * The banner after the platform sends the browser back: read once, then the
 * parameters leave the address so a reload does not show it again.
 */
function useConnectReturn() {
  const [params, setParams] = useSearchParams()
  const [banner, setBanner] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)
  useEffect(() => {
    const ok = params.get('platform_connected')
    const bad = params.get('platform_error')
    if (!ok && !bad) return
    const p = (ok ?? bad)!
    const label = p in ACCOUNT_LABEL ? ACCOUNT_LABEL[p as AccountPlatform] : p
    setBanner(ok ? { tone: 'ok', text: t('connect.ok', { platform: label }) } : { tone: 'err', text: connectError(label, params.get('reason')) })
    const next = new URLSearchParams(params)
    for (const k of ['platform_connected', 'platform_error', 'reason']) next.delete(k)
    setParams(next, { replace: true })
  }, [params, setParams])
  return [banner, () => setBanner(null)] as const
}

/** A media query, rendered (not just hidden) so only one layout exists at a time. */
export function useMedia(q: string) {
  const [m, setM] = useState(() => window.matchMedia(q).matches)
  useEffect(() => {
    const mq = window.matchMedia(q)
    const on = () => setM(mq.matches)
    on()
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [q])
  return m
}

/** phone < 768 ≤ tablet < 1024 ≤ desktop */
export type Screen = 'phone' | 'tablet' | 'desktop'
export function useScreen(): Screen {
  const md = useMedia('(min-width: 768px)')
  const lg = useMedia('(min-width: 1024px)')
  return lg ? 'desktop' : md ? 'tablet' : 'phone'
}

/**
 * Data and actions of the Live screens (Studio and Status): the view model from
 * the SSE view, saved destinations and profiles, toggles that stay busy until the
 * server reflects them, create / update / remove, bulk on / off (a PATCH per output:
 * there is no bulk endpoint yet), and the platform account connect round trip.
 */
export function useLiveScreen() {
  const { view, conn, at, user } = useSession()
  const qc = useQueryClient()
  const now = useNow(1000)
  const saved = useQuery({ queryKey: ['destinations'], queryFn: () => get<ApiDestination[]>('/api/destinations') })
  const profiles = useQuery({ queryKey: ['profiles'], queryFn: () => get<Profile[]>('/api/destination-profiles'), staleTime: Infinity })
  const [pending, setPending] = useState<Record<number, boolean>>({}) // destination → wanted enabled
  const [justSaved, setJustSaved] = useState<{ id: number; kind: 'reconnect' | 'fill' } | null>(null)
  // Bitrate fill (docs/bitrate-fill.md §4): only for users an admin turned it on for.
  const fill = !!user?.features?.fill
  const [err, setErr] = useState('')
  const [connectBanner, closeConnectBanner] = useConnectReturn()
  const [connecting, setConnecting] = useState<AccountPlatform | null>(null)
  // Back from the consent page out of the back/forward cache: the button is usable again.
  useEffect(() => {
    const show = (e: PageTransitionEvent) => e.persisted && setConnecting(null)
    window.addEventListener('pageshow', show)
    return () => window.removeEventListener('pageshow', show)
  }, [])

  // Connect a platform account: the platform's consent page in this tab; it comes back to / (useConnectReturn).
  const connect = async (p: AccountPlatform) => {
    setErr('')
    setConnecting(p)
    try {
      const { url } = await post<{ url: string }>(`/api/platform-accounts/${p}/connect`)
      if (new URL(url, window.location.href).protocol !== 'https:') throw new Error('not https')
      window.location.assign(url)
    } catch (e) {
      setConnecting(null)
      setErr(e instanceof ApiError && e.status === 409 ? t('connect.later', { platform: ACCOUNT_LABEL[p] }) : t('connect.cantStart', { platform: ACCOUNT_LABEL[p] }))
    }
  }

  // A toggle stays busy until the server reflects the click.
  useEffect(() => {
    if (!view) return
    setPending((p) => {
      let changed = false
      const next = { ...p }
      for (const o of view.outputs) {
        if (o.destination_id in next && next[o.destination_id] === o.enabled) {
          delete next[o.destination_id]
          changed = true
        }
      }
      return changed ? next : p
    })
  }, [view])

  useEffect(() => {
    if (justSaved === null) return
    const tm = setTimeout(() => setJustSaved(null), 3500)
    return () => clearTimeout(tm)
  }, [justSaved])

  // VERT-1: the vertical input of the main key (GET /api/stream-keys); when it was lost comes from the center
  // (input_effective.vertical_lost_at, VERT-LIVE)
  const keys = useQuery({ queryKey: ['stream-keys'], queryFn: () => get<StreamKey[]>('/api/stream-keys') })

  const m = useMemo(() => (view ? buildModel(view, { pending, now, at, saved: saved.data, profiles: profiles.data, fill, keys: keys.data }) : null), [view, pending, now, at, saved.data, profiles.data, fill, keys.data])

  const refresh = useCallback(() => qc.invalidateQueries({ queryKey: ['destinations'] }), [qc])

  const setEnabled = async (id: number, on: boolean, name: string) => {
    setPending((p) => ({ ...p, [id]: on }))
    try {
      await patch(`/api/destinations/${id}`, { enabled: on })
      return true
    } catch (e) {
      setErr(`${name}: ${(e as Error).message}`)
      setPending((p) => {
        const n = { ...p }
        delete n[id]
        return n
      })
      return false
    }
  }
  const toggle = async (id: number, enabled: boolean, name: string) => {
    setErr('')
    if (await setEnabled(id, !enabled, name)) void refresh()
  }
  /** All on / All off: one PATCH per output that is not there yet, in order; errors stay per output. */
  const setAll = async (on: boolean) => {
    if (!m) return
    setErr('')
    const todo = m.dests.filter((d) => d.enabled !== on && !d.busy)
    for (const d of todo) await setEnabled(d.id, on, d.name)
    void refresh()
  }
  const update = async (id: number, body: DestinationInput) => {
    await patch(`/api/destinations/${id}`, body)
    await refresh()
    // Only the bitrate or the hold changed: the output keeps its connection.
    const fillOnly = Object.keys(body).length > 0 && Object.keys(body).every((k) => k === 'fill_mode' || k === 'fill_quality' || k === 'source_grace_ms')
    setJustSaved({ id, kind: fillOnly ? 'fill' : 'reconnect' })
  }
  /** The key only (the fix in tips or a row): no "Saved" line, the state changes by itself. */
  const saveKey = async (id: number, key: string) => {
    await patch<ApiDestination>(`/api/destinations/${id}`, { key })
    await refresh()
  }
  const create = async (body: DestinationInput) => {
    const d = await post<ApiDestination>('/api/destinations', body)
    await refresh()
    return d
  }
  const remove = async (id: number) => {
    await del(`/api/destinations/${id}`)
    await refresh()
  }

  return {
    user, view, conn, now, m, err, setErr, fill,
    saved: saved.data, profiles: profiles.data,
    justSaved, connectBanner, closeConnectBanner, connecting, connect,
    toggle, setAll, update, saveKey, create, remove,
  }
}

export type LiveScreen = ReturnType<typeof useLiveScreen>

/** GET /api/platform-accounts: who can read chat (Chats) and whose stats show; failures mean "unknown". */
export function usePlatformAccounts() {
  return useQuery({ queryKey: ['platform-accounts'], queryFn: () => get<PlatformAccounts>('/api/platform-accounts'), staleTime: 60_000, retry: false })
}
